import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Home.css";

import { getStoredData } from "../../api/fetchData.jsx";
import RightUpdatesPanel from "../../components/RightUpdatesPanel/RightUpdatesPanel.jsx";

import ProjectSelectionView from "./components/ProjectSelectionView.jsx";
import DashboardView from "./components/DashboardView.jsx";

/* -------------------------------------------------------------------------- */
/*                               API constants                                */
/* -------------------------------------------------------------------------- */

const API_BASE_URL = "https://rohanbackend.axiomos.in";

const PROJECTS_API = `${API_BASE_URL}/projects`;

const LATEST_KEY_URL = `${API_BASE_URL}/latestKey:t9aZI42xdKRz3q7C3ooEU3fmX8v`;

/* -------------------------------------------------------------------------- */
/*                              Project helpers                               */
/* -------------------------------------------------------------------------- */

const getProjectIdentifier = (project) => {
  if (!project) {
    return "";
  }

  if (typeof project === "string" || typeof project === "number") {
    return String(project).trim();
  }

  if (typeof project !== "object") {
    return "";
  }

  return String(
    project._id ??
      project.id ??
      project.projectId ??
      project.projectID ??
      project.project_id ??
      project.projectCode ??
      project.code ??
      "",
  ).trim();
};

const isValidProject = (project) => {
  return Boolean(
    project && typeof project === "object" && getProjectIdentifier(project),
  );
};

const normalizeRole = (role) => {
  if (typeof role === "string") {
    return role
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  }

  return String(role?.roleName ?? role?.name ?? role?.title ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
};

const extractProjects = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const possibleArrays = [
    payload?.data,
    payload?.projects,
    payload?.results,
    payload?.records,
    payload?.items,
    payload?.docs,
  ];

  return possibleArrays.find(Array.isArray) || [];
};

const removeDuplicateProjects = (projects = []) => {
  const identifiers = new Set();

  return projects.filter((project) => {
    if (!isValidProject(project)) {
      return false;
    }

    const identifier = getProjectIdentifier(project);

    if (identifiers.has(identifier)) {
      return false;
    }

    identifiers.add(identifier);

    return true;
  });
};

/* -------------------------------------------------------------------------- */
/*                         Current rotating-key request                       */
/* -------------------------------------------------------------------------- */

const getCurrentRotatingKey = async (signal) => {
  /*
   * Do not trust the key stored in localStorage.
   * Always ask the backend for the current active key.
   */
  const response = await axios.get(LATEST_KEY_URL, {
    signal,

    headers: {
      Accept: "application/json",
    },
  });

  const currentKey = String(response?.data?.key || "").trim();

  if (!currentKey) {
    throw new Error("The backend did not return the current API key.");
  }

  /*
   * Saving is optional and is used only for debugging or other
   * parts of the application. This page never trusts this saved
   * value without checking the backend first.
   */
  localStorage.setItem("latestApiKey", currentKey);

  return currentKey;
};

/* -------------------------------------------------------------------------- */
/*                                   Home                                     */
/* -------------------------------------------------------------------------- */

const Home = () => {
  const adminFunctions = getStoredData()?.[0]?.Functions || [];

  const userDetails = useMemo(() => {
    try {
      const storedValue = localStorage.getItem("userDetails");

      const parsedValue = storedValue ? JSON.parse(storedValue) : {};

      return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
    } catch (error) {
      console.error("Unable to read user details:", error);

      return {};
    }
  }, []);

  const normalizedRole = useMemo(
    () => normalizeRole(userDetails?.role),
    [userDetails],
  );

  const isAdmin = useMemo(
    () => ["admin", "superadmin", "administrator"].includes(normalizedRole),
    [normalizedRole],
  );

  const storedAssignedProjects = useMemo(() => {
    return Array.isArray(userDetails?.project) ? userDetails.project : [];
  }, [userDetails]);

  const [projectCatalog, setProjectCatalog] = useState([]);

  const [projectsLoading, setProjectsLoading] = useState(true);

  const [projectsError, setProjectsError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedProject, setSelectedProject] = useState(() =>
    String(localStorage.getItem("selectedProject") || "").trim(),
  );

  const [viewMode, setViewMode] = useState(() => {
    const savedProject = String(
      localStorage.getItem("selectedProject") || "",
    ).trim();

    return savedProject ? "dashboard" : "select";
  });

  /* ------------------------------------------------------------------------ */
  /*                            Load project catalog                          */
  /* ------------------------------------------------------------------------ */

useEffect(() => {
  const controller = new AbortController();

  const loadProjects = async () => {
    let finalProjectsUrl = "";

    try {
      setProjectsLoading(true);
      setProjectsError("");

      localStorage.removeItem("latestApiKey");

      const keyResponse = await axios.get(LATEST_KEY_URL, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      });

      const currentKey = String(keyResponse?.data?.key || "").trim();

      if (!currentKey) {
        throw new Error("The backend did not return the current API key.");
      }

      if (controller.signal.aborted) {
        return;
      }

      localStorage.setItem("latestApiKey", currentKey);

      finalProjectsUrl = `${PROJECTS_API}:${currentKey}`;

      console.log("Calling projects:", {
        url: finalProjectsUrl,
        currentKey,
        origin: window.location.origin,
      });

      /*
       * Do not attach Authorization here.
       * The rotating key already protects this endpoint.
       *
       * Authorization triggers a CORS preflight request.
       */
      const response = await axios.get(finalProjectsUrl, {
        signal: controller.signal,

        headers: {
          Accept: "application/json",
        },
      });

      if (controller.signal.aborted) {
        return;
      }

      console.log("Complete projects response:", response?.data);

      const projects = extractProjects(response?.data);

      const cleanedProjects = removeDuplicateProjects(projects);

      console.log("Extracted projects:", cleanedProjects);

      console.table(
        cleanedProjects.map((project, index) => ({
          index,
          _id: project?._id || "",
          projectId: project?.projectId || project?.projectID || "",
          projectName: project?.projectName || project?.name || "",
          company: project?.company || "",
          location: project?.siteLocation || project?.location || "",
          status: project?.status || "",
        })),
      );

      setProjectCatalog(cleanedProjects);
    } catch (error) {
      const requestWasCancelled =
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        error?.name === "AbortError";

      if (requestWasCancelled) {
        return;
      }

      console.error("Projects request failed:", {
        url: error?.config?.url || finalProjectsUrl,
        method: error?.config?.method,
        requestHeaders: error?.config?.headers,
        status: error?.response?.status,
        response: error?.response?.data,
        responseHeaders: error?.response?.headers,
        code: error?.code,
        message: error?.message,
      });

      setProjectsError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to load projects.",
      );
    } finally {
      if (!controller.signal.aborted) {
        setProjectsLoading(false);
      }
    }
  };

  loadProjects();

  return () => {
    controller.abort();
  };
}, []);

  /* ------------------------------------------------------------------------ */
  /*                         Determine assigned projects                       */
  /* ------------------------------------------------------------------------ */

  const assignedProjects = useMemo(() => {
    const validStoredProjects = removeDuplicateProjects(storedAssignedProjects);

    /*
     * Admin receives all projects returned by the live endpoint.
     * If the API fails, use projects stored with the user session.
     */
    if (isAdmin) {
      return projectCatalog.length > 0 ? projectCatalog : validStoredProjects;
    }

    const assignedIdentifiers = new Set(
      storedAssignedProjects.map(getProjectIdentifier).filter(Boolean),
    );

    const hydratedProjects = projectCatalog.filter((project) => {
      const projectIdentifiers = [
        project?._id,
        project?.id,
        project?.projectId,
        project?.projectID,
        project?.project_id,
        project?.projectCode,
        project?.code,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);

      return projectIdentifiers.some((identifier) =>
        assignedIdentifiers.has(identifier),
      );
    });

    return removeDuplicateProjects([
      ...validStoredProjects,
      ...hydratedProjects,
    ]);
  }, [isAdmin, projectCatalog, storedAssignedProjects]);

  /* ------------------------------------------------------------------------ */
  /*                       Log complete component data                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    console.group("Home Project Data");

    console.log("Complete user details:", userDetails);

    console.log("User details JSON:", JSON.stringify(userDetails, null, 2));

    console.log("Normalized role:", normalizedRole);

    console.log("Is admin:", isAdmin);

    console.log("Stored assigned projects:", storedAssignedProjects);

    console.log("Live project catalog:", projectCatalog);

    console.log("Final assigned projects:", assignedProjects);

    console.log("Projects loading:", projectsLoading);

    console.log("Projects error:", projectsError);

    console.groupEnd();
  }, [
    userDetails,
    normalizedRole,
    isAdmin,
    storedAssignedProjects,
    projectCatalog,
    assignedProjects,
    projectsLoading,
    projectsError,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                            Selected project                              */
  /* ------------------------------------------------------------------------ */

  const activeProject = useMemo(() => {
    if (!selectedProject) {
      return null;
    }

    return (
      assignedProjects.find(
        (project) => getProjectIdentifier(project) === selectedProject,
      ) || null
    );
  }, [assignedProjects, selectedProject]);

  useEffect(() => {
    if (projectsLoading || !selectedProject) {
      return;
    }

    const projectStillExists = assignedProjects.some(
      (project) => getProjectIdentifier(project) === selectedProject,
    );

    if (!projectStillExists) {
      setSelectedProject("");
      setViewMode("select");

      localStorage.removeItem("selectedProject");
    }
  }, [assignedProjects, projectsLoading, selectedProject]);

  /* ------------------------------------------------------------------------ */
  /*                              Project search                              */
  /* ------------------------------------------------------------------------ */

  const filteredProjects = useMemo(() => {
    const normalizedSearch = String(searchTerm || "")
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return assignedProjects;
    }

    return assignedProjects.filter((project) => {
      const searchableValues = [
        project?.projectName,
        project?.name,
        project?.projectId,
        project?.projectID,
        project?._id,
        project?.company,
        project?.siteLocation,
        project?.location,
        project?.status,
        project?.description,
      ]
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).toLowerCase());

      return searchableValues.some((value) => value.includes(normalizedSearch));
    });
  }, [assignedProjects, searchTerm]);

  /* ------------------------------------------------------------------------ */
  /*                         Handle project selection                         */
  /* ------------------------------------------------------------------------ */

  const handleProjectSelect = (projectId) => {
    const normalizedProjectId = String(projectId || "").trim();

    /*
     * Empty value represents the portfolio dashboard.
     */
    if (!normalizedProjectId) {
      setSelectedProject("");

      localStorage.removeItem("selectedProject");

      setViewMode("dashboard");

      return;
    }

    const projectExists = assignedProjects.some(
      (project) => getProjectIdentifier(project) === normalizedProjectId,
    );

    if (!projectExists) {
      console.error(
        "Cannot select an invalid or unassigned project:",
        normalizedProjectId,
      );

      return;
    }

    setSelectedProject(normalizedProjectId);

    localStorage.setItem("selectedProject", normalizedProjectId);

    setViewMode("dashboard");
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                  */
  /* ------------------------------------------------------------------------ */

  const hasFallbackProjects = assignedProjects.length > 0;

  return (
    <div className="admin-dashboard">
      <RightUpdatesPanel className="right-updates" />

      <div className="home-content-container">
        {viewMode === "select" && (
          <>
            {projectsLoading && !hasFallbackProjects ? (
              <div className="no-project-message">Loading projects…</div>
            ) : projectsError && !hasFallbackProjects ? (
              <div className="no-project-message">{projectsError}</div>
            ) : (
              <>
                {projectsError && (
                  <div className="project-api-warning">
                    {projectsError} Showing projects assigned to your account.
                  </div>
                )}

                <ProjectSelectionView
                  userDetails={userDetails}
                  assignedProjects={assignedProjects}
                  filteredProjects={filteredProjects}
                  selectedProject={selectedProject}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onProjectSelect={handleProjectSelect}
                />
              </>
            )}
          </>
        )}

        {viewMode === "dashboard" && (
          <DashboardView
            functions={adminFunctions}
            selectedProject={selectedProject}
            activeProject={activeProject}
            onBackToProjects={() => setViewMode("select")}
          />
        )}
      </div>
    </div>
  );
};

export default Home;
